<template>
  <div class="sent-mail-page">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h4 class="card-title mb-0">
              <i class="bi bi-envelope-open me-2"></i>Sent Mail Delivery Monitor
            </h4>
            <div v-if="providerConfigured" class="d-flex align-items-center gap-2">
              <small v-if="lastSyncedAt" class="text-muted">Updated {{ formatDate(lastSyncedAt) }}</small>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                :disabled="syncing"
                title="Fetch the latest delivery results from the mail provider"
                @click="syncNow"
              >
                <span v-if="syncing" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                <i v-else class="bi bi-arrow-repeat me-1"></i>
                Sync now
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- Provider Health Alert -->
            <div v-if="!providerConfigured" class="alert alert-warning d-flex align-items-start" role="alert">
              <i class="bi bi-exclamation-triangle me-2 mt-1"></i>
              <div>
                <strong>Email Provider Not Configured</strong>
                <p class="mb-0 mt-1 small">
                  The email provider service is not currently configured. Please contact an administrator to set up the provider.
                </p>
              </div>
            </div>

            <!-- Backend Warning Alert (Dismissible) -->
            <div v-if="backendWarning && !warningDismissed" class="alert alert-info alert-dismissible fade show" role="alert">
              <i class="bi bi-info-circle me-2"></i>
              {{ backendWarning }}
              <button
                type="button"
                class="btn-close"
                aria-label="Close"
                @click="dismissWarning"
              ></button>
            </div>

            <!-- Data Scope Note -->
            <div class="alert alert-info mb-4">
              <i class="bi bi-info-circle me-2"></i>
              <small>This page shows only email messages sent by this platform through the configured provider.</small>
            </div>

            <!-- Statistics Cards -->
            <div v-if="providerConfigured && summary" class="row mb-4">
              <div class="col-md-2">
                <div class="stats-card pointer" @click="filterByStatus('all')">
                  <div class="stats-icon bg-primary">
                    <i class="bi bi-envelope"></i>
                  </div>
                  <div class="stats-content">
                    <h6>Sent</h6>
                    <h4>{{ summary.sent }}</h4>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="stats-card pointer" @click="filterByStatus('delivered')">
                  <div class="stats-icon bg-success">
                    <i class="bi bi-check-circle"></i>
                  </div>
                  <div class="stats-content">
                    <h6>Delivered</h6>
                    <h4>{{ summary.delivered }}</h4>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="stats-card pointer" @click="filterByStatus('opened')">
                  <div class="stats-icon bg-info text-dark">
                    <i class="bi bi-envelope-open"></i>
                  </div>
                  <div class="stats-content">
                    <h6>Opened</h6>
                    <h4>{{ summary.opened }}</h4>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="stats-card pointer" @click="filterByStatus('bounced')">
                  <div class="stats-icon bg-danger">
                    <i class="bi bi-exclamation-circle"></i>
                  </div>
                  <div class="stats-content">
                    <h6>Bounced</h6>
                    <h4>{{ summary.bounced }}</h4>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="stats-card pointer" @click="filterByStatus('invalid')">
                  <div class="stats-icon bg-warning text-dark">
                    <i class="bi bi-exclamation-triangle"></i>
                  </div>
                  <div class="stats-content">
                    <h6>Invalid</h6>
                    <h4>{{ summary.invalid }}</h4>
                  </div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="stats-card pointer" @click="filterByStatus('problems')">
                  <div class="stats-icon bg-secondary">
                    <i class="bi bi-dash-circle"></i>
                  </div>
                  <div class="stats-content">
                    <h6>Problems</h6>
                    <h4>{{ summary.bounced + summary.not_delivered + summary.invalid + summary.complaint }}</h4>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters and Controls -->
            <div v-if="providerConfigured" class="row mb-4">
              <div class="col-md-3">
                <label class="form-label small">Date From</label>
                <input
                  v-model="dateFrom"
                  type="date"
                  class="form-control form-control-sm"
                  @change="applyFilters"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label small">Date To</label>
                <input
                  v-model="dateTo"
                  type="date"
                  class="form-control form-control-sm"
                  @change="applyFilters"
                >
              </div>
              <div class="col-md-6">
                <label class="form-label small">Search</label>
                <div class="input-group input-group-sm">
                  <span class="input-group-text">
                    <i class="bi bi-search"></i>
                  </span>
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="form-control"
                    placeholder="Search by recipient or subject..."
                    @input="debouncedSearch"
                  >
                  <button
                    v-if="searchQuery"
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    @click="clearSearch"
                  >
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Category Tabs -->
            <div v-if="providerConfigured" class="row mb-4">
              <div class="col-12">
                <div class="btn-group" role="group">
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="selectedCategory === 'all' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="setCategory('all')"
                  >
                    All
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="selectedCategory === 'delivered' ? 'btn-success' : 'btn-outline-success'"
                    @click="setCategory('delivered')"
                  >
                    Delivered
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="selectedCategory === 'problems' ? 'btn-danger' : 'btn-outline-danger'"
                    @click="setCategory('problems')"
                  >
                    Problems
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="selectedCategory === 'opened' ? 'btn-info' : 'btn-outline-info'"
                    @click="setCategory('opened')"
                  >
                    Opened
                  </button>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2 text-muted">Loading mail events...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger" role="alert">
              <i class="bi bi-exclamation-circle me-2"></i>
              <strong>Error Loading Data:</strong> {{ error }}
            </div>

            <!-- Empty State -->
            <div v-else-if="events.length === 0" class="text-center py-5">
              <i class="bi bi-envelope display-4 text-muted"></i>
              <h6 class="text-muted mt-3">No Mail Events Found</h6>
              <p class="text-muted mb-0">
                <template v-if="searchQuery">No results match your search.</template>
                <template v-else-if="!lastSyncedAt">Delivery results have not been imported yet. Use Sync now to fetch them.</template>
                <template v-else>No mail events for the selected date range and category.</template>
              </p>
            </div>

            <!-- Events Table -->
            <div v-else class="table-responsive">
              <table class="table table-hover table-sm">
                <thead class="table-light">
                  <tr>
                    <th style="width: 22%">Recipient</th>
                    <th style="width: 24%">Subject</th>
                    <th style="width: 12%">Status</th>
                    <th style="width: 8%">Opened</th>
                    <th style="width: 14%">Date</th>
                    <th style="width: 16%">Reason</th>
                    <th style="width: 4%"><span class="visually-hidden">Details</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="event in events" :key="event.id" class="align-middle">
                    <td>
                      <small class="text-monospace">{{ event.to }}</small>
                    </td>
                    <td>
                      <small>{{ truncateText(event.subject, 50) }}</small>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="getStatusBadgeClass(event.status)"
                      >
                        <i :class="getStatusIcon(event.status)" class="me-1"></i>
                        {{ formatStatus(event.status) }}
                      </span>
                    </td>
                    <td>
                      <span v-if="event.opened" class="text-info" :title="openedTitle(event)">
                        <i class="bi bi-envelope-open-fill"></i>
                        <small v-if="event.open_count > 1" class="ms-1">{{ event.open_count }}</small>
                      </span>
                      <small v-else class="text-muted">-</small>
                    </td>
                    <td>
                      <small class="text-muted">{{ formatDate(event.event_date) }}</small>
                    </td>
                    <td>
                      <small
                        v-if="event.reason_summary || event.reason"
                        class="text-muted"
                        :title="event.reason_summary || event.reason"
                      >
                        {{ truncateText(event.reason_summary || event.reason, 60) }}
                      </small>
                      <small v-else class="text-muted">-</small>
                    </td>
                    <td class="text-end">
                      <button
                        type="button"
                        class="btn btn-link btn-sm p-0"
                        title="View delivery details"
                        aria-label="View delivery details"
                        @click="openDetails(event)"
                      >
                        <i class="bi bi-info-circle"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="providerConfigured && (events.length > 0 || currentPage > 1)" class="d-flex justify-content-between align-items-center mt-4">
              <small class="text-muted">
                Page {{ currentPage }} &middot; {{ events.length }} message{{ events.length === 1 ? '' : 's' }} shown
              </small>
              <nav>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <button class="page-link" :disabled="currentPage === 1" @click="previousPage">Previous</button>
                  </li>
                  <li class="page-item" :class="{ disabled: !hasMore }">
                    <button class="page-link" :disabled="!hasMore" @click="nextPage">Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delivery details dialog -->
    <div
      v-if="selectedEvent"
      class="modal fade show"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deliveryDetailsTitle"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      @click.self="closeDetails"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="deliveryDetailsTitle" class="modal-title">
              <i class="bi bi-info-circle me-2"></i>Delivery details
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeDetails"></button>
          </div>
          <div class="modal-body">
            <!-- What happened, and why, before anything else -->
            <div
              class="alert d-flex align-items-start"
              :class="'alert-' + statusSummary.variant"
              role="alert"
              data-test="status-summary"
            >
              <i :class="[statusSummary.icon, 'me-2', 'mt-1']"></i>
              <div class="flex-grow-1">
                <strong data-test="status-title">{{ statusSummary.title }}</strong>
                <p v-if="statusSummary.text" class="mb-0 mt-1" data-test="status-text">{{ statusSummary.text }}</p>
                <p v-if="selectedEvent.next_try" class="mb-0 mt-1 small" data-test="next-try">
                  Next attempt: {{ formatDate(selectedEvent.next_try) }}
                </p>
                <div v-if="hasTechnicalDetails">
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 mt-1"
                    :aria-expanded="showTechnical"
                    data-test="technical-toggle"
                    @click="showTechnical = !showTechnical"
                  >
                    {{ showTechnical ? 'Hide' : 'Show' }} technical details
                  </button>
                  <div v-if="showTechnical" class="technical-details mt-2" data-test="technical-details">
                    <div v-if="selectedEvent.reason_category" class="small">
                      <span class="text-muted">Category:</span> {{ selectedEvent.reason_category }}
                    </div>
                    <div v-if="selectedEvent.reason" class="small text-monospace">{{ selectedEvent.reason }}</div>
                  </div>
                </div>
              </div>
            </div>

            <dl class="row mb-4 small">
              <dt class="col-sm-3">Recipient</dt>
              <dd class="col-sm-9 text-monospace">{{ selectedEvent.to }}</dd>
              <dt class="col-sm-3">Subject</dt>
              <dd class="col-sm-9">{{ selectedEvent.subject || '-' }}</dd>
              <dt class="col-sm-3">Sent</dt>
              <dd class="col-sm-9">{{ formatDate(selectedEvent.event_date) }}</dd>
              <dt class="col-sm-3">Opened</dt>
              <dd class="col-sm-9">
                <template v-if="selectedEvent.opened_inferred">
                  Yes, first seen on {{ formatDate(selectedEvent.first_opened_at) }}
                  <div class="text-muted" data-test="inferred-open">
                    A link in the email was clicked, which proves it was opened, but the open itself was not
                    tracked (for example because the recipient's mail app blocks images).
                  </div>
                </template>
                <template v-else-if="selectedEvent.opened">
                  {{ selectedEvent.open_count }} time{{ selectedEvent.open_count === 1 ? '' : 's' }},
                  first on {{ formatDate(selectedEvent.first_opened_at) }}
                </template>
                <template v-else>Not recorded</template>
                <div class="text-muted">
                  Opens are approximate: some mail apps block images or load them automatically.
                </div>
              </dd>
              <dt class="col-sm-3">Links clicked</dt>
              <dd class="col-sm-9">{{ selectedEvent.clicked ? selectedEvent.click_count + ' click' + (selectedEvent.click_count === 1 ? '' : 's') : 'None' }}</dd>
              <dt class="col-sm-3">Provider</dt>
              <dd class="col-sm-9">{{ selectedEvent.provider }}</dd>
              <dt class="col-sm-3">Message ID</dt>
              <dd class="col-sm-9 text-monospace">{{ selectedEvent.id }}</dd>
            </dl>

            <h6 class="mb-3">Timeline</h6>
            <ul class="list-unstyled mb-0" data-test="timeline">
              <li v-for="(step, index) in selectedEvent.timeline" :key="index" class="d-flex mb-3">
                <i :class="[getEventIcon(step.type), getEventColor(step.type), 'me-3', 'mt-1']"></i>
                <div class="flex-grow-1">
                  <div class="d-flex justify-content-between flex-wrap">
                    <strong>{{ step.label }}</strong>
                    <small class="text-muted">{{ formatDate(step.date) }}</small>
                  </div>
                  <div v-if="step.detail" class="small">{{ step.detail }}</div>
                  <div v-if="step.message && step.message !== step.detail" class="small text-muted text-monospace">
                    Server response: {{ step.message }}
                  </div>
                  <div v-if="step.next_try" class="small text-muted">Next attempt: {{ formatDate(step.next_try) }}</div>
                </div>
              </li>
              <li v-if="!selectedEvent.timeline || selectedEvent.timeline.length === 0" class="text-muted small">
                No events recorded.
              </li>
            </ul>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetails">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import ProviderMailService from '../../../services/emails/ProviderMailService'

export default {
  name: 'SentMail',
  setup() {
    const loading = ref(true)
    const error = ref(null)
    const providerConfigured = ref(false)
    const summary = ref(null)
    const events = ref([])
    const backendWarning = ref(null)
    const warningDismissed = ref(false)

    const selectedEvent = ref(null)
    const showTechnical = ref(false)
    const syncing = ref(false)
    const lastSyncedAt = ref(null)

    const searchQuery = ref('')
    const selectedCategory = ref('all')
    const currentPage = ref(1)
    const perPage = ref(25)
    const hasMore = ref(false)

    const dateRange = ProviderMailService.getDateRangeLastDays(30)
    const dateFrom = ref(dateRange.from)
    const dateTo = ref(dateRange.to)

    let searchTimeout

    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        currentPage.value = 1
        refreshData()
      }, 300)
    }

    const dismissWarning = () => {
      warningDismissed.value = true
    }

    const clearSearch = () => {
      searchQuery.value = ''
      currentPage.value = 1
      refreshData()
    }

    const getStatusBadgeClass = (status) => {
      return ProviderMailService.getStatusBadgeClass(status)
    }

    const getStatusIcon = (status) => {
      return ProviderMailService.getStatusIcon(status)
    }

    const formatStatus = (status) => {
      return status.replace(/_/g, ' ').toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const truncateText = (text, length) => {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }

    const setCategory = (category) => {
      selectedCategory.value = category
      currentPage.value = 1
      refreshData()
    }

    // Date range changes start again from the first page.
    const applyFilters = () => {
      currentPage.value = 1
      refreshData()
    }

    const filterByStatus = (status) => {
      if (status === 'all' || status === 'delivered' || status === 'opened') {
        setCategory(status)
      } else {
        // bounced, invalid and problems all live in the Problems tab
        setCategory('problems')
      }
    }

    const getEventIcon = (type) => ProviderMailService.getEventIcon(type)
    const getEventColor = (type) => ProviderMailService.getEventColor(type)

    const openedTitle = (event) => {
      if (event.opened_inferred) {
        return `Opened: a link was clicked on ${formatDate(event.first_opened_at)}. The open itself was not tracked (for example because images are blocked).`
      }
      const times = `Opened ${event.open_count} time${event.open_count === 1 ? '' : 's'}`
      return event.first_opened_at ? `${times}, first on ${formatDate(event.first_opened_at)}` : times
    }

    // ---- details dialog -------------------------------------------------

    function onKeydown(keyEvent) {
      if (keyEvent.key === 'Escape') closeDetails()
    }

    function openDetails(event) {
      selectedEvent.value = event
      showTechnical.value = false
      document.addEventListener('keydown', onKeydown)
    }

    function closeDetails() {
      selectedEvent.value = null
      document.removeEventListener('keydown', onKeydown)
    }

    // What happened and why, shown above everything else in the dialog.
    const statusSummary = computed(() => ProviderMailService.describeStatus(selectedEvent.value))

    // The provider's own wording, only worth a toggle when it adds something.
    const hasTechnicalDetails = computed(() => {
      const event = selectedEvent.value
      if (!event) return false
      return Boolean(event.reason_category || (event.reason && event.reason !== event.reason_summary))
    })

    onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))

    const fetchProviderHealth = async () => {
      try {
        const response = await ProviderMailService.getProviderHealth()
        if (response.Success) {
          const data = response.Data
          providerConfigured.value = data.configured === true
          lastSyncedAt.value = data.last_synced_at || null
        }
      } catch (err) {
        console.error('Error fetching provider health:', err)
        providerConfigured.value = false
      }
    }

    const fetchSummary = async () => {
      try {
        const response = await ProviderMailService.getProviderSummary({
          from: dateFrom.value,
          to: dateTo.value
        })
        if (response.Success) {
          summary.value = response.Data.data || response.Data
          if (response.Data.warning) {
            backendWarning.value = response.Data.warning
            warningDismissed.value = false
          }
        }
      } catch (err) {
        console.error('Error fetching provider summary:', err)
      }
    }

    const fetchEvents = async () => {
      try {
        loading.value = true
        error.value = null

        const response = await ProviderMailService.getProviderEvents({
          category: selectedCategory.value,
          search: searchQuery.value,
          from: dateFrom.value,
          to: dateTo.value,
          page: currentPage.value,
          per_page: perPage.value
        })

        if (response.Success) {
          const data = response.Data
          events.value = data.data || []
          hasMore.value = data.has_more === true
          if (data.warning && !backendWarning.value) {
            backendWarning.value = data.warning
          }
        }
      } catch (err) {
        console.error('Error fetching events:', err)
        error.value = err.message || 'Failed to load mail events'
      } finally {
        loading.value = false
      }
    }

    const refreshData = async () => {
      await Promise.all([
        fetchSummary(),
        fetchEvents()
      ])
    }

    // Pull the latest results from the provider into the stored history, then reload.
    const syncNow = async () => {
      syncing.value = true
      try {
        const response = await ProviderMailService.syncNow()
        const results = response?.Data?.results || []
        const failed = results.find(result => result.status === 'error')

        if (failed || response?.Success === false) {
          backendWarning.value = `Could not refresh from the mail provider: ${failed?.message || response?.Message || 'unknown error'}`
          warningDismissed.value = false
        }

        await Promise.all([fetchProviderHealth(), refreshData()])
      } catch (err) {
        console.error('Error syncing delivery results:', err)
        backendWarning.value = 'Could not refresh from the mail provider.'
        warningDismissed.value = false
      } finally {
        syncing.value = false
      }
    }

    const previousPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
        fetchEvents()
      }
    }

    const nextPage = () => {
      if (hasMore.value) {
        currentPage.value++
        fetchEvents()
      }
    }

    onMounted(async () => {
      await fetchProviderHealth()
      if (providerConfigured.value) {
        await refreshData()
      } else {
        loading.value = false
      }
    })

    return {
      loading,
      error,
      providerConfigured,
      summary,
      events,
      backendWarning,
      warningDismissed,
      searchQuery,
      selectedCategory,
      currentPage,
      perPage,
      hasMore,
      dateFrom,
      dateTo,
      selectedEvent,
      showTechnical,
      syncing,
      lastSyncedAt,
      statusSummary,
      hasTechnicalDetails,
      debouncedSearch,
      dismissWarning,
      clearSearch,
      getStatusBadgeClass,
      getStatusIcon,
      formatStatus,
      formatDate,
      truncateText,
      filterByStatus,
      setCategory,
      applyFilters,
      getEventIcon,
      getEventColor,
      openedTitle,
      openDetails,
      closeDetails,
      syncNow,
      refreshData,
      previousPage,
      nextPage
    }
  }
}
</script>

<style scoped>
.stats-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
}

.stats-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #d0d0d0;
  transform: translateY(-2px);
}

.stats-card.pointer {
  cursor: pointer;
}

.stats-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 0.375rem;
  margin-right: 1rem;
  color: white;
  font-size: 1.5rem;
}

.stats-content h6 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.stats-content h4 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #212529;
  margin: 0;
}

.text-monospace {
  font-family: 'Courier New', monospace;
  word-break: break-all;
}

.table-responsive {
  border-radius: 0.375rem;
  border: 1px solid #e0e0e0;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.625rem;
}

.technical-details {
  padding: 0.5rem 0.75rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  word-break: break-word;
}
</style>
